import { useRef, useCallback, useEffect } from 'react';
import { AgentService } from '@/services/agent';
import { useAgentStore, STREAMING_RESET } from '@/stores/agentStore';
import { restoreBackendMessage } from '../helpers';

interface StreamPollingOptions {
  onComplete?: (content: string, msgId: string) => void;
  onFailed?: (error: string | null, msgId: string) => void;
}

export function useStreamPolling(options: StreamPollingOptions = {}) {
  const activePollsRef = useRef<Map<string, ReturnType<typeof setInterval>>>(new Map());
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const startPolling = useCallback((
    sessionId: string,
    assistantMsgId: string,
    intervalMs: number = 3000,
  ) => {
    const pollKey = `${sessionId}:${assistantMsgId}`;
    if (activePollsRef.current.has(pollKey)) return;

    const pollInterval = setInterval(async () => {
      try {
        const status = await AgentService.getStreamStatus(sessionId, assistantMsgId);
        if (status.state === 'completed' && status.content) {
          clearInterval(pollInterval);
          activePollsRef.current.delete(pollKey);

          // 🔴 v5-#4: 从后端状态获取正确的 msg_type，避免搜索/确认消息降级为 text
          const msgType = (status as any).msgType || (status as any).type || 'text';
          const finalMsg = restoreBackendMessage({
            id: assistantMsgId,
            role: 'assistant',
            content: status.content,
            type: msgType,
            processLogs: status.processLogs,
            introText: status.introText,
            metadata: (status as any).metadata,
          });
          // 🔴 F-15: 原子操作 — 替换/追加消息 + STREAMING_RESET，与 finalizeAssistantMessage 一致
          // 避免 setMessages 和 resetStreamingState 之间的渲染间隙导致搜索卡片双重渲染
          useAgentStore.setState((state) => {
            const idx = state.messages.findIndex(m => m.id === assistantMsgId);
            const updated = [...state.messages];
            if (idx >= 0) {
              updated[idx] = finalMsg;
            } else {
              updated.push(finalMsg);
            }
            return {
              messages: updated,
              ...STREAMING_RESET,
            };
          });

          if (finalMsg.text !== status.content) {
            try {
              // 🔴 v6-#1: 使用 finalMsg.type 而非硬编码 'text'，避免搜索/确认消息降级
              await AgentService.updateMessageContent(assistantMsgId, sessionId, finalMsg.text, finalMsg.type || 'text');
            } catch (err) {
              console.warn('[StreamPolling] Failed to sync cleaned content:', err);
            }
          }

          optionsRef.current.onComplete?.(status.content, assistantMsgId);
        } else if (status.state === 'failed') {
          clearInterval(pollInterval);
          activePollsRef.current.delete(pollKey);
          // 🔴 v9-#3: 移除此处的 resetStreamingState，由 onFailed 回调统一负责
          // useAgentStore.getState().resetStreamingState();
          optionsRef.current.onFailed?.(status.error ?? null, assistantMsgId);
        } else if (status.state === 'running' && status.contentLength !== undefined) {
          // 🔴 P0-2: 增强检测 SSE 活跃状态，避免 polling 与 SSE 竞态写入
          const store = useAgentStore.getState();
          const builder = store.activeReplyBuilder;

          // 搜索/生成确认场景：完全跳过 streamingText 更新（由 onSearchResult 负责）
          if (builder && (builder.type === 'search' || builder.type === 'generation_confirm')) {
            return;
          }

          // P0-2: 检测 SSE 是否仍活跃（最近 5 秒内有 chunk 到达）
          // 🔴 F-16: 增加 isThinking 辅助判断，避免慢速思考场景误判 SSE 中断
          // 大模型思考时 chunk 间隔可能 > 5 秒，此时 isThinking=true 表示仍在正常工作
          const lastChunkAt = store.lastChunkReceivedAt;
          const sseIsActive = lastChunkAt !== null && (Date.now() - lastChunkAt < 5000);
          const isThinkingActive = store.isThinking;

          if (sseIsActive || isThinkingActive) {
            // SSE 仍活跃 或 模型正在思考中：由 onChunk 负责更新文本，polling 仅监控不写入
            return;
          }

          // SSE 已中断：polling 提供进度提示
          // 🔴 F-14: 同步写入 builder.text，避免 streamingText 与 builder.text 不一致
          // P0-1 规定 streamingText 为统一写入入口，但 polling 是独立于 SSE 的路径
          // 必须同时更新 builder.text，否则 finalizeAssistantMessage 读 builder.text 会丢失进度文本
          if (builder !== null) {
            // 有活跃 builder：追加进度提示，同时同步 builder.text
            const current = store.streamingText || '';
            if (!current.includes('[后台生成中]')) {
              // 仅在首次写入 progress text，避免累积多条进度提示
              const progressText = current + '\n[后台生成中... 已生成 ' + status.contentLength + ' 字符]';
              store.setStreamingText(progressText);
              // 🔴 F-14: 同步更新 builder.text，保持与 streamingText 一致
              store.updateActiveReplyBuilder({ text: progressText });
            } else {
              // 已有 progress text：仅同步 builder.text（不含 progress 部分）
              // 这保证 builder.text 与 SSE 累积文本一致
              store.updateActiveReplyBuilder({ text: current });
            }
          } else if (!store.isThinking && !store.isLoading) {
            // 无 builder：仅显示后台生成进度
            store.setStreamingText(`[后台生成中... 已生成 ${status.contentLength} 字符]`);
          }
        }
      } catch (pollErr) {
        console.warn('[StreamPolling] Poll error:', pollErr);
      }
    }, intervalMs);

    activePollsRef.current.set(pollKey, pollInterval);
  }, []);

  const stopPolling = useCallback((sessionId: string, assistantMsgId: string) => {
    const pollKey = `${sessionId}:${assistantMsgId}`;
    const pollInterval = activePollsRef.current.get(pollKey);
    if (pollInterval) {
      clearInterval(pollInterval);
      activePollsRef.current.delete(pollKey);
    }
  }, []);

  const stopAllPolling = useCallback(() => {
    for (const [, pollInterval] of activePollsRef.current) {
      clearInterval(pollInterval);
    }
    activePollsRef.current.clear();
  }, []);

  useEffect(() => {
    return () => {
      stopAllPolling();
    };
  }, []);

  return { startPolling, stopPolling, stopAllPolling };
}
